/**
 * Ravian Size Switcher
 * Handles switching between different Ravian product sizes (52", 60", 72")
 * This creates a 4th variant option experience by linking multiple products
 * 
 * When a size is selected:
 * 1. Shows loading overlay
 * 2. Fetches product data to find matching variant
 * 3. Redirects to new product with variant preserved
 */

(function() {
  'use strict';
  
  const RavianSizeSwitcher = {
    selectors: {
      sizeSelector: '[data-ravian-size-selector]',
      sizeOptions: '.js-size-option',
      sizeInfo: '[data-size-info]',
      productForm: '[data-product-form]',
      variantSelectors: '[data-single-option-selector]',
      addToCartButton: '[data-add-to-cart]',
      productPrice: '.product-price',
      productImages: '.product-detail__images-container'
    },
    
    init: function() {
      const sizeSelector = document.querySelector(this.selectors.sizeSelector);
      if (!sizeSelector) return;
      
      // Listen for size changes
      const sizeOptions = sizeSelector.querySelectorAll(this.selectors.sizeOptions);
      sizeOptions.forEach(option => {
        option.addEventListener('change', this.handleSizeChange.bind(this));
      });
      
      // Store current variant selections
      this.storeCurrentSelections();
    },
    
    storeCurrentSelections: function() {
      // Store current variant selections to restore after size change
      const selections = {};
      const variantSelectors = document.querySelectorAll(this.selectors.variantSelectors);
      
      variantSelectors.forEach(selector => {
        const fieldset = selector.closest('fieldset');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const optionName = legend.textContent.trim().split('-')[0].trim();
            const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
              selections[optionName] = checkedInput.value;
            }
          }
        } else {
          // Handle dropdown selectors
          const select = selector.querySelector('select');
          if (select) {
            const label = select.getAttribute('aria-label') || '';
            selections[label] = select.value;
          }
        }
      });
      
      this.currentSelections = selections;
    },
    
    handleSizeChange: function(event) {
      const selectedOption = event.target;
      const newSize = selectedOption.value;
      const newHandle = selectedOption.dataset.productHandle;
      
      // Update size info display
      this.updateSizeInfo(newSize);
      
      // Store current selections before switching
      this.storeCurrentSelections();
      
      // Show loading state
      this.showLoadingState();
      
      // Try to find matching variant based on current selections
      fetch(`/products/${encodeURIComponent(newHandle)}?view=json`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(productData => {
          let redirectUrl = `/products/${newHandle}`;
          
          // Try to find matching variant
          if (productData && productData.variants) {
            const matchingVariantId = this.findMatchingVariant(productData.variants, this.currentSelections, productData);
            if (matchingVariantId) {
              redirectUrl += `?variant=${matchingVariantId}`;
            }
          }
          
          // Redirect with variant preserved
          window.location.href = redirectUrl;
        })
        .catch(error => {
          // If anything fails, just redirect to the product
          console.error('Error fetching product data:', error);
          window.location.href = `/products/${newHandle}`;
        });
    },
    
    updateSizeInfo: function(size) {
      const sizeInfoElements = document.querySelectorAll(this.selectors.sizeInfo);
      sizeInfoElements.forEach(element => {
        if (element.dataset.sizeInfo === size) {
          element.style.display = 'block';
        } else {
          element.style.display = 'none';
        }
      });
    },
    
    showLoadingState: function() {
      // Add loading class to body
      document.body.classList.add('ravian-size-switching');
      
      // Disable form elements
      const formElements = document.querySelectorAll(
        `${this.selectors.variantSelectors} input, 
         ${this.selectors.variantSelectors} select, 
         ${this.selectors.addToCartButton}`
      );
      formElements.forEach(element => {
        element.disabled = true;
      });
    },
    
    hideLoadingState: function() {
      document.body.classList.remove('ravian-size-switching');
      
      // Re-enable form elements (they'll be fresh from the new content anyway)
      const formElements = document.querySelectorAll(
        `${this.selectors.variantSelectors} input, 
         ${this.selectors.variantSelectors} select, 
         ${this.selectors.addToCartButton}`
      );
      formElements.forEach(element => {
        element.disabled = false;
      });
    },
    
    
    findMatchingVariant: function(variants, selections, productData) {
      // Try to find a variant that matches the stored selections
      let bestMatch = null;
      let bestMatchScore = -1;
      
      variants.forEach(variant => {
        if (!variant.available) return;
        
        let matchScore = 0;
        let totalOptions = 0;
        
        // Check each option
        variant.options.forEach((optionValue, index) => {
          const optionName = productData.options[index];
          
          // Skip size-related options
          if (optionName && (optionName.toLowerCase().includes('size') || 
                            optionName.toLowerCase().includes('inch'))) {
            return;
          }
          
          totalOptions++;
          
          // Check if this option matches our stored selection
          if (selections[optionName] === optionValue) {
            matchScore++;
          }
        });
        
        // Calculate match percentage
        const matchPercentage = totalOptions > 0 ? matchScore / totalOptions : 0;
        
        // Update best match if this variant is better
        if (matchPercentage > bestMatchScore) {
          bestMatchScore = matchPercentage;
          bestMatch = variant;
        }
      });
      
      return bestMatch ? bestMatch.id : null;
    }
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => RavianSizeSwitcher.init.call(RavianSizeSwitcher));
  } else {
    RavianSizeSwitcher.init.call(RavianSizeSwitcher);
  }
  
  // Also initialize on Shopify theme editor events
  document.addEventListener('shopify:section:load', () => RavianSizeSwitcher.init.call(RavianSizeSwitcher));
  
  // Make RavianSizeSwitcher globally available
  window.RavianSizeSwitcher = RavianSizeSwitcher;
})();


